import {
  apply,
  botAction,
  createMatch,
  toPrivate,
  toPublic,
  toSeatView,
  validateRules,
  type Action,
  type BotLevel,
  type MatchState,
  type Rng,
  type RulesConfig,
  type Seat,
  type TaggedEvent,
} from '@mendikot/engine';
import {
  animationBudgetMs,
  parseSeatKey,
  seatKey,
  TIMING,
  TURN_SCOPED,
  type BotTakeoverReason,
  type Envelope,
  type ErrorCode,
  type EventEnvelope,
  type HostEvent,
  type PushType,
  type Reply,
  type RoomPrivateView,
  type RoomPublicView,
  type SeatInfo,
  type SeatKey,
} from '@mendikot/protocol';
import {
  decodeCore,
  encodeSecure,
  encodeViews,
  type CoreState,
  type EncodedCore,
  type HandArchive,
} from './doc';

export type Actor = { kind: 'user'; uid: string } | { kind: 'system' };

export interface HostContext {
  actor: Actor;
  /** Server time in ms. */
  now: number;
  rng: Rng;
  botLevel?: BotLevel;
}

export type Effect =
  | { kind: 'push'; uids: string[]; type: PushType; roomId: string; roomCode: string }
  | { kind: 'releaseCode'; code: string; roomId: string };

export type HandleResult =
  | { kind: 'commit'; core: EncodedCore; reply: Reply; effects: Effect[]; state: CoreState }
  | { kind: 'abort'; reply: Reply };

const DEDUPE_RING = 16;
const BOT_NAMES = ['Asha', 'Ravi', 'Meera', 'Kabir', 'Priya', 'Arjun', 'Neha', 'Vikram'];

const fail = (code: ErrorCode, message?: string): Reply => ({
  ok: false,
  code,
  ...(message ? { message } : {}),
});
const abort = (reply: Reply): HandleResult => ({ kind: 'abort', reply });

/** Seat that must act now: the reserver during the Band reserve step, otherwise the active seat. */
export function actingSeat(match: MatchState): Seat {
  return match.hand.phase === 'RESERVE' ? match.hand.trump.reserverSeat! : match.hand.active;
}

/** Mutable working copy for one command; produces the next core document on commit. */
class Tx {
  readonly state: CoreState;
  readonly events: { to: TaggedEvent['to']; e: HostEvent }[] = [];
  readonly effects: Effect[] = [];
  changed = false;

  constructor(
    base: CoreState,
    readonly ctx: HostContext,
  ) {
    this.state = JSON.parse(JSON.stringify(base)) as CoreState;
  }

  get room() {
    return this.state.room;
  }

  pub(e: HostEvent): void {
    this.events.push({ to: 'public', e });
    this.changed = true;
  }

  touch(): void {
    this.changed = true;
  }

  push(type: PushType, uids: (string | null | undefined)[]): void {
    const targets = uids.filter((u): u is string => typeof u === 'string');
    if (targets.length === 0) return;
    this.effects.push({
      kind: 'push',
      type,
      uids: targets,
      roomId: this.state.meta.roomId,
      roomCode: this.state.meta.code,
    });
  }

  commit(reply: Extract<Reply, { ok: true }>, requestId: string | null): HandleResult {
    const { state, ctx } = this;
    const fromV = state.room.version;
    const toV = fromV + 1;
    state.room.version = toV;
    state.room.updatedAt = ctx.now;
    state.meta.lastActivity = ctx.now;
    const finalReply: Reply = { ...reply, version: toV };

    if (ctx.actor.kind === 'user' && requestId) {
      const ring = state.dedupe[ctx.actor.uid] ?? [];
      ring.push({ id: requestId, reply: finalReply });
      state.dedupe[ctx.actor.uid] = ring.slice(-DEDUPE_RING);
    }

    const tagged: (EventEnvelope & { to: TaggedEvent['to'] })[] = this.events.map((ev, n) => ({
      n,
      to: ev.to,
      e: ev.e,
    }));
    const batchFor = (to: TaggedEvent['to']) => ({
      fromV,
      toV,
      events: tagged.filter((ev) => ev.to === to).map(({ n, e }) => ({ n, e })),
    });

    const room = state.room;
    const publicView: RoomPublicView = {
      version: toV,
      roomId: state.meta.roomId,
      code: state.meta.code,
      hostUid: state.meta.hostUid,
      status: state.meta.status,
      rules: room.rules,
      seats: room.seats,
      game: state.match ? toPublic(state.match) : null,
      turnDeadline: room.turnDeadline,
      nextHandAt: room.nextHandAt,
      nextVotes: room.nextVotes,
      rematchVotes: room.rematchVotes,
      nudgeKey: room.nudgeKey,
      abandonReason: room.abandonReason,
      updatedAt: ctx.now,
      batch: batchFor('public'),
    };
    const privateViews = {} as Record<SeatKey, RoomPrivateView>;
    for (let seat = 0; seat < room.rules.playerCount; seat++) {
      const priv = state.match ? toPrivate(state.match, seat) : { hand: [], reserved: null };
      privateViews[seatKey(seat)] = {
        version: toV,
        seat,
        hand: priv.hand,
        reserved: priv.reserved,
        batch: batchFor(seat),
      };
    }
    const core: EncodedCore = {
      meta: state.meta,
      members: state.members,
      ...encodeViews({ public: publicView, private: privateViews }),
      secure: encodeSecure(state),
    };
    return { kind: 'commit', core, reply: finalReply, effects: this.effects, state };
  }
}

export interface CreateRoomParams {
  roomId: string;
  code: string;
  host: { uid: string; name: string; avatarId: string };
  rules: RulesConfig;
  now: number;
}

/** Builds the initial core document for a new room with the host seated at seat 0. */
export function createRoom(params: CreateRoomParams): Extract<HandleResult, { kind: 'commit' }> {
  const errors = validateRules(params.rules);
  if (errors.length > 0) throw new Error(errors.join('; '));
  const players = params.rules.playerCount;
  const seats: (SeatInfo | null)[] = Array.from({ length: players }, () => null);
  seats[0] = humanSeat(0, params.host.uid, params.host.name, params.host.avatarId);
  const base: CoreState = {
    meta: {
      roomId: params.roomId,
      code: params.code,
      hostUid: params.host.uid,
      status: 'LOBBY',
      createdAt: params.now,
      lastActivity: params.now,
      matchId: null,
      lastScoredHandId: null,
    },
    members: { [params.host.uid]: seatKey(0) },
    room: {
      version: 0,
      updatedAt: params.now,
      rules: { ...params.rules },
      seats,
      turnDeadline: null,
      nextHandAt: null,
      nextVotes: [],
      rematchVotes: [],
      nudgeKey: null,
      abandonReason: null,
      matchCount: 0,
      matchStartedAt: null,
    },
    match: null,
    dedupe: {},
    lastHand: null,
  };
  const tx = new Tx(base, { actor: { kind: 'system' }, now: params.now, rng: { int: () => 0 } });
  tx.touch();
  const result = tx.commit(
    { ok: true, version: 0, data: { roomId: params.roomId, code: params.code, seat: 0 } },
    null,
  );
  if (result.kind !== 'commit') throw new Error('createRoom failed');
  return result;
}

function humanSeat(seat: Seat, uid: string, name: string, avatarId: string): SeatInfo {
  return {
    seat,
    uid,
    name,
    avatarId,
    kind: 'human',
    controller: 'human',
    ready: false,
    timeouts: 0,
  };
}

function botSeat(seat: Seat): SeatInfo {
  return {
    seat,
    uid: null,
    name: BOT_NAMES[seat % BOT_NAMES.length]!,
    avatarId: `bot-${seat % BOT_NAMES.length}`,
    kind: 'bot',
    controller: 'bot',
    ready: true,
    timeouts: 0,
  };
}

/**
 * Pure authoritative command handler (plan §2b). Runs inside the RTDB transaction on the server and
 * in memory for practice mode. Never trusts a client-supplied seat: it is derived from membership.
 */
export function handle(raw: EncodedCore | null, env: Envelope, ctx: HostContext): HandleResult {
  if (!raw) return abort(fail('NOT_FOUND'));
  const state = decodeCore(raw);
  const uid = ctx.actor.kind === 'user' ? ctx.actor.uid : null;

  if (uid) {
    const previous = state.dedupe[uid]?.find((d) => d.id === env.meta.requestId);
    if (previous) return abort(previous.reply);
  }
  if (state.meta.status === 'ABANDONED') return abort(fail('ROOM_CLOSED'));

  if (TURN_SCOPED.has(env.type) && env.expectedVersion !== state.room.version) {
    return abort(fail('STALE_VERSION'));
  }

  const tx = new Tx(state, ctx);
  const reply = dispatch(tx, env, uid);
  if (!reply.ok) return abort(reply);
  if (!tx.changed) return abort({ ...reply, version: state.room.version });
  return tx.commit(reply, env.meta.requestId);
}

function dispatch(tx: Tx, env: Envelope, uid: string | null): Reply {
  if (!uid) return fail('UNAUTHENTICATED');
  if (env.type === 'room.join') return join(tx, uid, env as Envelope<'room.join'>);

  const key = tx.state.members[uid];
  const seat = key ? parseSeatKey(key) : null;
  if (seat === null) return fail('NOT_MEMBER');
  const isHost = tx.state.meta.hostUid === uid;
  const ok: Reply = { ok: true, version: 0 };

  switch (env.type) {
    case 'room.leave':
      return leave(tx, uid, seat);
    case 'room.configure':
      return configure(
        tx,
        isHost,
        (env as Envelope<'room.configure'>).payload.rules as RulesConfig,
      );
    case 'seat.take':
      return takeSeat(tx, uid, seat, (env as Envelope<'seat.take'>).payload.seat);
    case 'seat.ready': {
      if (tx.state.meta.status !== 'LOBBY') return fail('WRONG_PHASE');
      const info = tx.room.seats[seat]!;
      const ready = (env as Envelope<'seat.ready'>).payload.ready;
      if (info.ready !== ready) {
        info.ready = ready;
        tx.touch();
      }
      return ok;
    }
    case 'seat.addBot':
      return addBot(tx, isHost, (env as Envelope<'seat.addBot'>).payload.seat);
    case 'seat.removeBot':
      return removeBot(tx, isHost, (env as Envelope<'seat.removeBot'>).payload.seat);
    case 'match.start':
      return start(tx, isHost);
    case 'trump.reserve':
      return gameAction(tx, seat, {
        type: 'RESERVE_TRUMP',
        cardId: (env as Envelope<'trump.reserve'>).payload.cardId,
      });
    case 'card.play':
      return gameAction(tx, seat, {
        type: 'PLAY_CARD',
        cardId: (env as Envelope<'card.play'>).payload.cardId,
      });
    case 'trump.revealRequest':
      return gameAction(tx, seat, { type: 'REQUEST_REVEAL' });
    case 'turn.claimTimeout':
      return claimTimeout(tx);
    case 'turn.nudge':
      return nudge(tx, seat);
    case 'hand.next':
      return nextHand(tx, seat);
    case 'player.reconnect':
      return reconnect(tx, seat);
    case 'rematch.vote':
      return rematch(tx, seat, (env as Envelope<'rematch.vote'>).payload.yes);
    default:
      return fail('UNKNOWN_COMMAND');
  }
}

function join(tx: Tx, uid: string, env: Envelope<'room.join'>): Reply {
  const { state } = tx;
  const { name, avatarId } = env.payload;
  const data = { roomId: state.meta.roomId, code: state.meta.code };
  const existing = state.members[uid];
  if (existing) {
    const seat = parseSeatKey(existing)!;
    const info = tx.room.seats[seat]!;
    if (state.meta.status === 'LOBBY' && (info.name !== name || info.avatarId !== avatarId)) {
      info.name = name;
      info.avatarId = avatarId;
      tx.touch();
    }
    return { ok: true, version: 0, data: { ...data, seat, rejoined: true } };
  }
  if (state.meta.status !== 'LOBBY') return fail('ROOM_IN_PROGRESS');
  const seats = tx.room.seats;
  const requested = env.payload.seat;
  let seat =
    requested !== undefined && requested < seats.length && seats[requested] === null
      ? requested
      : -1;
  if (seat < 0) seat = seats.findIndex((s) => s === null);
  if (seat < 0) return fail('ROOM_FULL');
  seats[seat] = humanSeat(seat, uid, name, avatarId);
  state.members[uid] = seatKey(seat);
  tx.touch();
  if (seats.every((s) => s !== null)) tx.push('table_ready', [state.meta.hostUid]);
  return { ok: true, version: 0, data: { ...data, seat } };
}

function leave(tx: Tx, uid: string, seat: Seat): Reply {
  const { state } = tx;
  if (state.meta.status === 'PLAYING') {
    takeover(tx, seat, 'LEFT');
    afterGame(tx);
    return { ok: true, version: 0 };
  }
  tx.room.seats[seat] = null;
  delete state.members[uid];
  tx.touch();
  const humans = tx.room.seats.filter((s): s is SeatInfo => s?.kind === 'human');
  if (humans.length === 0) {
    abandon(tx, 'EMPTY');
  } else if (state.meta.hostUid === uid) {
    state.meta.hostUid = humans[0]!.uid!;
  }
  return { ok: true, version: 0 };
}

function configure(tx: Tx, isHost: boolean, rules: RulesConfig): Reply {
  if (!isHost) return fail('NOT_HOST');
  if (tx.state.meta.status !== 'LOBBY') return fail('WRONG_PHASE');
  const errors = validateRules(rules);
  if (errors.length > 0) return fail('INVALID_RULES', errors.join('; '));
  const seats = tx.room.seats;
  if (seats.slice(rules.playerCount).some((s) => s !== null))
    return fail('BAD_SEAT', 'Seats beyond the new table size are occupied');
  const resized: (SeatInfo | null)[] = Array.from(
    { length: rules.playerCount },
    (_, i) => seats[i] ?? null,
  );
  for (const info of resized) if (info?.kind === 'human') info.ready = false;
  tx.room.seats = resized;
  tx.room.rules = { ...rules };
  tx.touch();
  return { ok: true, version: 0 };
}

function takeSeat(tx: Tx, uid: string, from: Seat, to: Seat): Reply {
  if (tx.state.meta.status !== 'LOBBY') return fail('WRONG_PHASE');
  const seats = tx.room.seats;
  if (to >= seats.length) return fail('BAD_SEAT');
  if (to === from) return { ok: true, version: 0 };
  if (seats[to] !== null) return fail('SEAT_TAKEN');
  const info = seats[from]!;
  seats[from] = null;
  seats[to] = { ...info, seat: to, ready: false };
  tx.state.members[uid] = seatKey(to);
  tx.touch();
  return { ok: true, version: 0, data: { seat: to } };
}

function addBot(tx: Tx, isHost: boolean, seat: Seat): Reply {
  if (!isHost) return fail('NOT_HOST');
  if (tx.state.meta.status !== 'LOBBY') return fail('WRONG_PHASE');
  if (seat >= tx.room.seats.length) return fail('BAD_SEAT');
  if (tx.room.seats[seat] !== null) return fail('SEAT_TAKEN');
  tx.room.seats[seat] = botSeat(seat);
  tx.touch();
  return { ok: true, version: 0 };
}

function removeBot(tx: Tx, isHost: boolean, seat: Seat): Reply {
  if (!isHost) return fail('NOT_HOST');
  if (tx.state.meta.status !== 'LOBBY') return fail('WRONG_PHASE');
  if (tx.room.seats[seat]?.kind !== 'bot') return fail('BAD_SEAT');
  tx.room.seats[seat] = null;
  tx.touch();
  return { ok: true, version: 0 };
}

function start(tx: Tx, isHost: boolean): Reply {
  if (!isHost) return fail('NOT_HOST');
  if (tx.state.meta.status !== 'LOBBY') return fail('WRONG_PHASE');
  const seats = tx.room.seats;
  if (seats.some((s) => s === null)) return fail('SEATS_EMPTY');
  const notReady = seats.filter(
    (s) => s!.kind === 'human' && !s!.ready && s!.uid !== tx.state.meta.hostUid,
  );
  if (notReady.length > 0) return fail('NOT_READY');
  startMatch(tx);
  return { ok: true, version: 0, data: { matchId: tx.state.meta.matchId } };
}

function startMatch(tx: Tx): void {
  const { state, ctx } = tx;
  const room = state.room;
  room.matchCount += 1;
  const matchId = `${state.meta.roomId}-m${room.matchCount}`;
  const created = createMatch({ rules: room.rules, matchId, rng: ctx.rng });
  state.match = created.state;
  state.meta.status = 'PLAYING';
  state.meta.matchId = matchId;
  room.matchStartedAt = ctx.now;
  room.rematchVotes = [];
  room.nudgeKey = null;
  for (const info of room.seats) {
    if (!info) continue;
    info.timeouts = 0;
    info.controller = info.kind === 'bot' ? 'bot' : 'human';
  }
  tx.pub({ t: 'MATCH_STARTED', matchId, rules: room.rules });
  tx.events.push(...created.events);
  afterGame(tx);
}

function gameAction(tx: Tx, seat: Seat, action: Action): Reply {
  const { state } = tx;
  if (state.meta.status !== 'PLAYING' || !state.match) return fail('WRONG_PHASE');
  const result = apply(state.match, action, seat, tx.ctx.rng);
  if (!result.ok) return fail(result.code);
  state.match = result.state;
  tx.events.push(...result.events);
  tx.touch();
  const info = tx.room.seats[seat]!;
  info.timeouts = 0;
  afterGame(tx);
  return { ok: true, version: 0 };
}

function claimTimeout(tx: Tx): Reply {
  const { state, ctx } = tx;
  const match = state.match;
  if (state.meta.status !== 'PLAYING' || !match || match.phase !== 'IN_HAND')
    return fail('WRONG_PHASE');
  const deadline = tx.room.turnDeadline;
  if (deadline === null) return fail('ALREADY_RESOLVED');
  if (ctx.now + TIMING.CLAIM_TOLERANCE_MS < deadline) return fail('TOO_EARLY');
  const seat = actingSeat(match);
  const info = tx.room.seats[seat]!;
  if (info.controller === 'bot') return fail('ALREADY_RESOLVED');

  tx.pub({ t: 'AUTO_PLAYED', seat, reason: 'TIMEOUT' });
  const action = botAction(toSeatView(match, seat), ctx.rng, 'normal');
  const result = apply(match, action, seat, ctx.rng);
  if (!result.ok) throw new Error(`Fallback move rejected: ${result.code}`);
  state.match = result.state;
  tx.events.push(...result.events);
  info.timeouts += 1;
  if (info.timeouts >= TIMING.MAX_TIMEOUTS_BEFORE_BOT) takeover(tx, seat, 'TIMEOUTS');
  afterGame(tx);
  return { ok: true, version: 0 };
}

function nudge(tx: Tx, seat: Seat): Reply {
  const match = tx.state.match;
  if (tx.state.meta.status !== 'PLAYING' || !match || match.phase !== 'IN_HAND')
    return fail('WRONG_PHASE');
  const target = actingSeat(match);
  const info = tx.room.seats[target]!;
  if (target === seat || info.kind !== 'human' || info.controller !== 'human')
    return fail('ILLEGAL_ACTION');
  const key = `${match.hand.handId}:${match.hand.trickIndex}:${match.hand.trick.length}`;
  if (tx.room.nudgeKey === key) return fail('ALREADY_RESOLVED');
  tx.room.nudgeKey = key;
  tx.touch();
  tx.push('turn_reminder', [info.uid]);
  return { ok: true, version: 0 };
}

function nextHand(tx: Tx, seat: Seat): Reply {
  const { state, ctx } = tx;
  const match = state.match;
  if (state.meta.status !== 'PLAYING' || !match || match.phase !== 'HAND_OVER')
    return fail('WRONG_PHASE');
  const room = tx.room;
  room.nextVotes[seat] = true;
  tx.touch();
  const everyone = room.seats.every((s, i) => s!.controller === 'bot' || room.nextVotes[i]);
  const dueAt = room.nextHandAt ?? 0;
  if (!everyone && ctx.now + TIMING.CLAIM_TOLERANCE_MS < dueAt) return { ok: true, version: 0 };

  const result = apply(match, { type: 'NEXT_HAND' }, 'system', ctx.rng);
  if (!result.ok) return fail(result.code);
  state.match = result.state;
  tx.events.push(...result.events);
  afterGame(tx);
  return { ok: true, version: 0 };
}

function reconnect(tx: Tx, seat: Seat): Reply {
  const info = tx.room.seats[seat]!;
  if (info.kind !== 'human' || info.controller === 'human') return { ok: true, version: 0 };
  info.controller = 'human';
  info.timeouts = 0;
  tx.pub({ t: 'PLAYER_RETURNED', seat });
  const match = tx.state.match;
  if (
    match &&
    match.phase === 'IN_HAND' &&
    actingSeat(match) === seat &&
    match.rules.actionTimeoutSec > 0
  ) {
    tx.room.turnDeadline = tx.ctx.now + TIMING.RECONNECT_TURN_MS;
  }
  return { ok: true, version: 0 };
}

function rematch(tx: Tx, seat: Seat, yes: boolean): Reply {
  const { state } = tx;
  if (state.meta.status !== 'MATCH_OVER') return fail('WRONG_PHASE');
  const room = tx.room;
  room.rematchVotes[seat] = yes;
  tx.touch();
  if (!yes) {
    state.meta.status = 'LOBBY';
    state.match = null;
    room.rematchVotes = [];
    for (const info of room.seats) if (info?.kind === 'human') info.ready = false;
    return { ok: true, version: 0 };
  }
  const allYes = room.seats.every((s, i) => s!.kind === 'bot' || room.rematchVotes[i] === true);
  if (allYes) startMatch(tx);
  return { ok: true, version: 0 };
}

function takeover(tx: Tx, seat: Seat, reason: BotTakeoverReason): void {
  const info = tx.room.seats[seat]!;
  if (info.controller === 'bot') return;
  info.controller = 'bot';
  tx.pub({ t: 'BOT_TAKEOVER', seat, reason });
}

function abandon(tx: Tx, reason: string): void {
  const { state } = tx;
  state.meta.status = 'ABANDONED';
  tx.room.abandonReason = reason;
  tx.room.turnDeadline = null;
  tx.room.nextHandAt = null;
  tx.pub({ t: 'ROOM_ABANDONED', reason });
  tx.effects.push({ kind: 'releaseCode', code: state.meta.code, roomId: state.meta.roomId });
}

/** Chains bot-controlled turns, archives scored hands and sets deadlines (plan §2d). */
function afterGame(tx: Tx): void {
  const { state, ctx } = tx;
  let match = state.match;
  if (!match) return;
  const room = tx.room;

  for (let guard = 0; match.phase === 'IN_HAND' && guard < 2000; guard++) {
    const seat = actingSeat(match);
    if (room.seats[seat]?.controller !== 'bot') break;
    const action = botAction(toSeatView(match, seat), ctx.rng, ctx.botLevel ?? 'normal');
    const result = apply(match, action, seat, ctx.rng);
    if (!result.ok) throw new Error(`Bot move rejected: ${result.code}`);
    match = result.state;
    tx.events.push(...result.events);
  }
  state.match = match;
  tx.touch();

  const scored = tx.events.find((ev) => ev.e.t === 'HAND_SCORED');
  if (scored && scored.e.t === 'HAND_SCORED') {
    const archive: HandArchive = {
      matchId: match.matchId,
      handId: scored.e.result.handId,
      rules: match.rules,
      result: scored.e.result,
      tricks: match.hand.tricks,
      seats: room.seats.map((s, seat) => ({
        seat,
        uid: s?.uid ?? null,
        kind: s?.kind ?? 'bot',
        controller: s?.controller ?? 'bot',
        name: s?.name ?? '',
      })),
      score: match.score,
      matchWinner: match.winner,
      at: ctx.now,
    };
    state.lastHand = archive;
    state.meta.lastScoredHandId = archive.handId;
  }

  const budget = animationBudgetMs(tx.events.map((ev) => ev.e));
  if (match.phase === 'IN_HAND') {
    const seat = actingSeat(match);
    const timed = room.seats[seat]?.controller === 'human' && match.rules.actionTimeoutSec > 0;
    room.turnDeadline = timed ? ctx.now + match.rules.actionTimeoutSec * 1000 + budget : null;
    room.nextHandAt = null;
    room.nextVotes = [];
  } else if (match.phase === 'HAND_OVER') {
    room.turnDeadline = null;
    room.nextHandAt = ctx.now + TIMING.RESULT_PAUSE_MS + budget;
    room.nextVotes = room.seats.map(() => false);
  } else {
    room.turnDeadline = null;
    room.nextHandAt = null;
    state.meta.status = 'MATCH_OVER';
    room.rematchVotes = room.seats.map((s) => (s?.kind === 'bot' ? true : null));
    tx.push(
      'rematch_request',
      room.seats.map((s) => s?.uid),
    );
  }
}

/** System: a player's presence stayed offline past the grace period (Cloud Task, plan §2d). */
export function markAway(
  raw: EncodedCore | null,
  uid: string,
  ctx: Omit<HostContext, 'actor'>,
): HandleResult {
  if (!raw) return abort(fail('NOT_FOUND'));
  const state = decodeCore(raw);
  const key = state.members[uid];
  const seat = key ? parseSeatKey(key) : null;
  if (seat === null) return abort(fail('NOT_MEMBER'));
  if (state.meta.status !== 'PLAYING') return abort(fail('WRONG_PHASE'));
  const tx = new Tx(state, { ...ctx, actor: { kind: 'system' } });
  const info = tx.room.seats[seat]!;
  if (info.controller === 'bot') return abort({ ok: true, version: state.room.version });
  takeover(tx, seat, 'DISCONNECTED');
  afterGame(tx);
  tx.push('game_waiting', [uid]);
  return tx.commit({ ok: true, version: 0 }, null);
}

/** System: the sweeper closes an idle room and releases its code. */
export function abandonRoom(
  raw: EncodedCore | null,
  reason: string,
  ctx: Omit<HostContext, 'actor'>,
): HandleResult {
  if (!raw) return abort(fail('NOT_FOUND'));
  const state = decodeCore(raw);
  if (state.meta.status === 'ABANDONED') return abort({ ok: true, version: state.room.version });
  const tx = new Tx(state, { ...ctx, actor: { kind: 'system' } });
  abandon(tx, reason);
  return tx.commit({ ok: true, version: 0 }, null);
}

export type { SeatKey };
